import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Play } from 'lucide-react';

const Hero = () => {
  const { t } = useTranslation();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Grid */}
      <div className="absolute inset-0 cyber-grid opacity-30"></div>
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-secondary-400 rounded-full animate-ping animation-delay-200"></div>
        <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-primary-400 rounded-full animate-ping animation-delay-400"></div>
        <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-secondary-300 rounded-full animate-ping animation-delay-600"></div>
        <div className="absolute bottom-1/3 right-1/4 w-1 h-1 bg-primary-300 rounded-full animate-ping"></div>
      </div>

      <div className="container-custom mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="text-center lg:text-left">
            <div className="inline-block mb-6">
              <span className="bg-gradient-to-r from-secondary-500/20 to-primary-500/20 border border-secondary-500/30 rounded-full px-4 py-2 text-sm font-semibold text-secondary-400">
                {t('hero.tagline')}
              </span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-gradient">
                {t('hero.title')}
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl">
              {t('hero.subtitle')}
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button 
                onClick={() => scrollToSection('contact')}
                className="btn-primary group flex items-center justify-center space-x-2"
              >
                <span>{t('hero.cta')}</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              
              <button 
                onClick={() => scrollToSection('services')}
                className="btn-secondary group flex items-center justify-center space-x-2"
              >
                <Play className="w-5 h-5" />
                <span>{t('hero.discover')}</span>
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-white/10">
              <div className="text-center lg:text-left">
                <div className="text-2xl font-bold text-gradient">100+</div>
                <div className="text-sm text-gray-400">{t('hero.stats.projects')}</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl font-bold text-gradient">5+</div>
                <div className="text-sm text-gray-400">{t('hero.stats.experience')}</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl font-bold text-gradient">24/7</div>
                <div className="text-sm text-gray-400">{t('hero.stats.support')}</div>
              </div>
            </div>
          </div>

          {/* Visual Element */}
          <div className="relative flex items-center justify-center">
            <div className="relative w-80 h-80 lg:w-96 lg:h-96">
              {/* Main Logo */}
              <div className="absolute inset-0 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tech-consulting-1/artifacts/fne2dgjf_ChatGPT%20Image%2026%20aou%CC%82t%202025%20a%CC%80%2014_49_28.png" 
                  alt="Winstep FZE" 
                  className="w-64 h-64 lg:w-80 lg:h-80 object-contain animate-float filter drop-shadow-2xl"
                />
              </div>
              
              {/* Orbiting Elements */}
              <div className="absolute inset-0 animate-spin" style={{ animationDuration: '20s' }}>
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-secondary-400 rounded-full shadow-lg shadow-secondary-400/50"></div>
              </div>
              
              <div className="absolute inset-0 animate-spin" style={{ animationDuration: '15s', animationDirection: 'reverse' }}>
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-3 h-3 bg-primary-400 rounded-full shadow-lg shadow-primary-400/50"></div>
              </div>
              
              <div className="absolute inset-0 animate-spin" style={{ animationDuration: '25s' }}>
                <div className="absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-secondary-300 rounded-full shadow-lg shadow-secondary-300/50"></div>
              </div>

              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-radial from-secondary-500/20 via-transparent to-transparent blur-3xl"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white rounded-full animate-bounce mt-2"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;